import { Injectable, NgZone, inject } from '@angular/core';

import { BUG_REPORT_TRIGGER_ATTRIBUTE } from './bug-report-launcher.service';
import { hoverLabel } from './element-description';

/**
 * Pointer events interceptés pendant la sélection : sans cela, le clic qui désigne l'élément
 * déclencherait aussi son action (navigation, ouverture d'un menu, rotation de la vue 3D…).
 */
const BLOCKED_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'dblclick', 'contextmenu'] as const;

/** Attribut du cadre de survol : il ne doit jamais être lui-même désigné. */
const OVERLAY_ATTRIBUTE = 'data-bug-report-overlay';

/** Issue d'une sélection : l'élément désigné, ou l'abandon. */
export interface PickHandlers {
  picked(element: Element): void;
  cancelled(): void;
}

/**
 * Sélection d'un élément de la page, comme dans l'inspecteur du navigateur : le cadre suit la
 * souris ou le focus, et le clic — ou Entrée, ou Espace — désigne l'élément encadré. Au clavier,
 * ↑ élargit le cadre à l'élément parent, pour viser un bloc qui ne prend pas le focus, et ↓
 * revient en arrière ; Échap abandonne. Pendant la sélection, la page ne reçoit ni ces clics ni
 * ces touches : désigner un bouton ne le déclenche pas.
 *
 * Les écouteurs tournent hors zone Angular — chaque mouvement de souris déclencherait sinon une
 * détection de changements sur toute l'application — et le cadre est déplacé directement dans le
 * DOM. Seuls les rappels de fin de sélection reviennent dans la zone. Les déclencheurs
 * (BUG_REPORT_TRIGGER_ATTRIBUTE) et le signalement lui-même ne sont jamais désignés.
 */
@Injectable({ providedIn: 'root' })
export class ElementPicker {
  private readonly zone = inject(NgZone);

  private handlers: PickHandlers | null = null;
  private overlay: { frame: HTMLElement; label: HTMLElement } | null = null;
  /** Élément encadré, à la souris ou au clavier. */
  private framed: Element | null = null;
  /** Éléments quittés par ↑ pour leur parent, que ↓ retrouve. */
  private readonly widened: Element[] = [];

  // Références stables pour pouvoir retirer les écouteurs.
  private readonly onMove = (event: MouseEvent) => this.trackPointer(event);
  private readonly onClick = (event: MouseEvent) => this.pick(event);
  private readonly onBlocked = (event: Event) => this.swallow(event);
  private readonly onFocus = (event: FocusEvent) => this.trackFocus(event);
  private readonly onKeyDown = (event: KeyboardEvent) => this.keyDown(event);
  private readonly onKeyUp = (event: KeyboardEvent) => this.keyUp(event);

  start(handlers: PickHandlers): void {
    this.stop();
    this.handlers = handlers;
    document.body.classList.add('bug-report-picking');

    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onMove, true);
      document.addEventListener('click', this.onClick, true);
      document.addEventListener('focusin', this.onFocus, true);
      document.addEventListener('keydown', this.onKeyDown, true);
      document.addEventListener('keyup', this.onKeyUp, true);
      for (const type of BLOCKED_EVENTS) {
        document.addEventListener(type, this.onBlocked, true);
      }
    });
  }

  /** Arrête la sélection sans rien rapporter. */
  stop(): void {
    this.handlers = null;
    document.body.classList.remove('bug-report-picking');
    document.removeEventListener('mousemove', this.onMove, true);
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('focusin', this.onFocus, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    document.removeEventListener('keyup', this.onKeyUp, true);
    for (const type of BLOCKED_EVENTS) {
      document.removeEventListener(type, this.onBlocked, true);
    }
    this.hide();
  }

  /** Termine la sélection et rapporte son issue, de retour dans la zone Angular. */
  private finish(element: Element | null): void {
    const handlers = this.handlers;
    this.stop();
    this.zone.run(() => (element ? handlers?.picked(element) : handlers?.cancelled()));
  }

  private trackPointer(event: MouseEvent): void {
    const element = this.pickableTarget(event);
    if (element) {
      this.frame(element);
    } else {
      this.hide();
    }
  }

  private trackFocus(event: FocusEvent): void {
    const element = this.pickableTarget(event);
    if (element) {
      this.frame(element);
    }
  }

  private pick(event: MouseEvent): void {
    const element = this.pickableTarget(event);
    // Clic sur un déclencheur de la navbar : on le laisse passer, il sert à annuler.
    if (element) {
      this.block(event);
      this.finish(element);
    }
  }

  private keyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        this.block(event);
        this.finish(null);
        return;
      case 'ArrowUp': {
        const parent = this.framed?.parentElement;
        if (this.framed && parent && parent !== document.body && this.isPickable(parent)) {
          this.block(event);
          this.widened.push(this.framed);
          this.frame(parent, true);
        }
        return;
      }
      case 'ArrowDown': {
        const child = this.widened.pop();
        if (child) {
          this.block(event);
          this.frame(child, true);
        }
        return;
      }
      case 'Enter':
        if (this.framed && !this.onTrigger(event)) {
          this.block(event);
          this.finish(this.framed);
        }
        return;
      case ' ':
        // Espace active un bouton au relâchement : la sélection attend keyup.
        if (this.framed && !this.onTrigger(event)) {
          this.block(event);
        }
        return;
    }
  }

  private keyUp(event: KeyboardEvent): void {
    if (event.key === ' ' && this.framed && !this.onTrigger(event)) {
      this.block(event);
      this.finish(this.framed);
    }
  }

  /**
   * Vrai quand la touche vise un déclencheur de la navbar : Entrée ou Espace l'activent alors
   * normalement, ce qui annule la sélection, au lieu de désigner l'élément encadré auparavant.
   */
  private onTrigger(event: Event): boolean {
    return event.target instanceof Element && !!event.target.closest(`[${BUG_REPORT_TRIGGER_ATTRIBUTE}]`);
  }

  /** L'élément visé, sauf s'il fait partie du signalement lui-même. */
  private pickableTarget(event: Event): Element | null {
    const element = event.target instanceof Element ? event.target : null;
    return element && this.isPickable(element) ? element : null;
  }

  private isPickable(element: Element): boolean {
    return !element.closest(`app-bug-report, [${BUG_REPORT_TRIGGER_ATTRIBUTE}], [${OVERLAY_ATTRIBUTE}]`);
  }

  private swallow(event: Event): void {
    if (this.pickableTarget(event)) {
      this.block(event);
    }
  }

  private block(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  /**
   * Encadre l'élément. Sans `keepWidened`, il vient de la souris ou du focus : l'historique de ↑
   * repart de lui.
   */
  private frame(element: Element, keepWidened = false): void {
    this.framed = element;
    if (!keepWidened) {
      this.widened.length = 0;
    }

    const { frame, label } = this.ensureOverlay();
    const rect = element.getBoundingClientRect();
    frame.style.display = 'block';
    frame.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
    frame.style.width = `${rect.width}px`;
    frame.style.height = `${rect.height}px`;

    label.textContent = `${hoverLabel(element)}  ${Math.round(rect.width)} × ${Math.round(rect.height)}`;
    // Étiquette sous le cadre quand il touche le haut de l'écran, au-dessus sinon.
    label.classList.toggle('bug-report__highlight-label--below', rect.top < 28);
  }

  private hide(): void {
    this.framed = null;
    this.widened.length = 0;
    if (this.overlay) {
      this.overlay.frame.style.display = 'none';
    }
  }

  /** Cadre de survol, créé à la première sélection ; ses styles sont ceux de app-bug-report. */
  private ensureOverlay(): { frame: HTMLElement; label: HTMLElement } {
    if (!this.overlay) {
      const frame = document.createElement('div');
      frame.className = 'bug-report__highlight';
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute(OVERLAY_ATTRIBUTE, '');
      const label = document.createElement('span');
      label.className = 'bug-report__highlight-label';
      frame.appendChild(label);
      document.body.appendChild(frame);
      this.overlay = { frame, label };
    }
    return this.overlay;
  }
}
